import { defineAsyncComponent } from 'vue'
import Multiselect from 'vue-multiselect'
import AsideSkeleton from '@/components/common/aside-sidebar/aside-skeleton.vue'
import KtAsideProvider from '@/components/common/kt-aside-provider.vue'
import KtHeader from '@/components/common/kt-header.vue'
import KtSidebarProvider from '@/components/common/kt-sidebar-provider.vue'
import CustomTooltip from '@/components/custom-tooltip.vue'
import SearchInput from '@/components/ui/search-input.vue'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/shadcn/components/ui/card'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from '@/shadcn/components/ui/input-group/index.js'
import { Input } from '@/shadcn/components/ui/input/index.js'
import { SidebarTrigger } from '@/shadcn/components/ui/sidebar'
import { Textarea } from '@/shadcn/components/ui/textarea/index.js'
import ImportModuleError from '../../components/common/import-module-error.vue'
import RelativeTime from '../../components/common/relative-time.vue'

// wrap with handling dynamic imports error
// https://sentry.io/answers/failed-to-fetch-dynamically-imported-module-in-react/
export function isImportModuleLoadError(error) {
  const message = error?.message ?? ''

  return error?.name === 'ChunkLoadError'
    || message.includes('Unable to preload CSS')
    || message.includes('Failed to fetch dynamically imported module')
    || message.includes('Importing a module script failed')
    || message.includes('error loading dynamically imported module')
}

function defineAsyncComponentWrapper(loader) {
  async function importFn() {
    try {
      return await loader()
    }
    catch (error) {
      if (isImportModuleLoadError(error)) {
        return ImportModuleError
      }

      return Promise.reject(error)
    }
  }

  return defineAsyncComponent(() => importFn())
}

export function setupGlobalComponents(app) {
  app.component('Multiselect', Multiselect)
  app.component('RelativeTime', RelativeTime)

  app.component('KtHeader', KtHeader)
  app.component('KtSidebarProvider', KtSidebarProvider)
  app.component('KtAsideProvider', KtAsideProvider)
  app.component('SidebarTrigger', SidebarTrigger)
  app.component('AsideSkeleton', AsideSkeleton)
  app.component('Card', Card)
  app.component('CardHeader', CardHeader)
  app.component('CardTitle', CardTitle)
  app.component('CardContent', CardContent)
  app.component('CardDescription', CardDescription)
  app.component('CardFooter', CardFooter)
  app.component('SearchInput', SearchInput)
  app.component('InputGroup', InputGroup)
  app.component('InputGroupAddon', InputGroupAddon)
  app.component('InputGroupText', InputGroupText)
  app.component('InputGroupInput', InputGroupInput)
  app.component('CustomInput', Input)
  app.component('CustomTextarea', Textarea)
  app.component('CustomTooltip', CustomTooltip)

  app.component('AuthSocialNav', defineAsyncComponentWrapper(() => import('../../components/auth/social-nav.vue')))
  app.component('AuthPasswordInput', defineAsyncComponentWrapper(() => import('../../components/auth/password-input.vue')))
  app.component('AuthRegister', defineAsyncComponentWrapper(() => import('../../components/auth/register/register.vue')))
  app.component('MessageBubble', defineAsyncComponentWrapper(() => import('../../components/message-bubble.vue')))
  app.component('ImpersonationIndicator', defineAsyncComponentWrapper(() => import('../../components/impersonation-indicator.vue')))

  app.component('VideoShow', defineAsyncComponentWrapper(() => import('../../components/video/show.vue')))
  app.component('VideoShowReply', defineAsyncComponentWrapper(() => import('../../components/video/show-reply.vue')))
  app.component('VideoAppShare', defineAsyncComponentWrapper(() => import('../../components/video/app-share.vue')))
  app.component('VideoEmbed', defineAsyncComponentWrapper(() => import('../../components/video/embed.vue')))
  app.component('VideoEmbedCreateWrapper', defineAsyncComponentWrapper(() => import('../../components/video/embed-create-wrapper.vue')))
  app.component('VideoCreateWithPermissionModal', defineAsyncComponentWrapper(() => import('../../components/video/create-with-permission-modal.vue')))
  app.component('VideoPasswordForm', defineAsyncComponentWrapper(() => import('../../components/video/password-form.vue')))
  app.component('VideoPortfolio', defineAsyncComponentWrapper(() => import('../../components/video/portfolio.vue')))

  app.component('ShowcaseShow', defineAsyncComponentWrapper(() => import('../../components/showcase/show.vue')))
  app.component('ShowcaseEmbed', defineAsyncComponentWrapper(() => import('../../components/showcase/embed.vue')))

  app.component('FormRender', defineAsyncComponentWrapper(() => import('../../components/form/render.vue')))
  app.component('BootModal', defineAsyncComponentWrapper(() => import('../../components/common/boot-modal.vue')))
  app.component('ForceRouterLink', defineAsyncComponentWrapper(() => import('../../components/common/force-router-link.vue')))
  app.component('VideoJs', defineAsyncComponentWrapper(() => import('../../components/common/video-js.vue')))
  app.component('VideoPreviewList', defineAsyncComponentWrapper(() => import('../../components/common/video-preview-list.vue')))
  app.component('WorldMapJoin', defineAsyncComponentWrapper(() => import('../../components/common/world-map-join.vue')))
  app.component('FeatureLocker', defineAsyncComponentWrapper(() => import('../../components/common/feature-locker.vue')))
  app.component('ExitIntent', defineAsyncComponentWrapper(() => import('../../components/common/exit-intent.vue')))
  app.component('EmptyTableMessage', defineAsyncComponentWrapper(() => import('../../components/common/empty-table-message.vue')))
  app.component('Paginator', defineAsyncComponentWrapper(() => import('../../components/common/paginator.vue')))
  app.component('V4Navbar', defineAsyncComponentWrapper(() => import('../../components/common/v4-navbar.vue')))

  app.component('LandingPageBuilderGrapesjs', defineAsyncComponentWrapper(() => import('../../components/landing-page/grapesjs-builder.vue')))
  app.component('LandingPageBuilderUnlayer', defineAsyncComponentWrapper(() => import('../../components/landing-page/unlayer-builder.vue')))

  app.component('GuestVideoCreate', defineAsyncComponentWrapper(() => import('../../components/guest-video/create.vue')))
  app.component('GuestVideoShow', defineAsyncComponentWrapper(() => import('../../components/guest-video/show.vue')))
  app.component('GuestVideoEdit', defineAsyncComponentWrapper(() => import('../../components/guest-video/edit.vue')))

  app.component('EmailTemplateBuilderGrapesjs', defineAsyncComponentWrapper(() => import('../../components/email-template/grapesjs-builder.vue')))
  app.component('EmailTemplateBuilderUnlayer', defineAsyncComponentWrapper(() => import('../../components/email-template/unlayer-builder.vue')))

  app.component('VideoTemplateBuilder', defineAsyncComponentWrapper(() => import('../../components/video-template/builder.vue')))
  app.component('VideoSection', defineAsyncComponentWrapper(() => import('../../components/video-template/video-section.vue')))
  app.component('VideoTitle', defineAsyncComponentWrapper(() => import('../../components/video-template/video-title.vue')))
  app.component('VideoDescription', defineAsyncComponentWrapper(() => import('../../components/video-template/video-description.vue')))
  app.component('CtaSection', defineAsyncComponentWrapper(() => import('../../components/video-template/cta-section.vue')))
  app.component('ContactSection', defineAsyncComponentWrapper(() => import('../../components/video-template/contact-section.vue')))
  app.component('FormSection', defineAsyncComponentWrapper(() => import('../../components/video-template/form-section.vue')))
  app.component('CalendarSection', defineAsyncComponentWrapper(() => import('../../components/video-template/calendar-section.vue')))

  app.component('TeamSubscription', defineAsyncComponentWrapper(() => import('../../components/team-subscription/index.vue')))
  app.component('SwitchAccount', defineAsyncComponentWrapper(() => import('../../modules/standalones/switch-account.vue')))

  app.component('PagesMarketplace', defineAsyncComponentWrapper(() => import('../../components/pages/marketplace.vue')))
  app.component('PagesSia', defineAsyncComponentWrapper(() => import('../../components/pages/sia/sia.vue')))
  app.component('SiaShow', defineAsyncComponentWrapper(() => import('../../components/sia/show.vue')))
  app.component('LandingPageExitModal', defineAsyncComponentWrapper(() => import('../../components/landing-page/landing-page-exit-modal.vue')))
}
